import { CommonModule as NgCommonModule } from '@angular/common';
import { ChangeDetectionStrategy, Component, Input, inject, input, output } from '@angular/core';
import { RouterModule } from '@angular/router';
import { IconKey } from '@common/classes/icons';
import { Breadcrumb, NavigationService } from '@common/services/navigation.service';
import { NgIconComponent } from '@ng-icons/core';
import { TranslateModule } from '@ngx-translate/core';
import { DateInputsModule } from '@progress/kendo-angular-dateinputs';
import { TooltipModule } from '@progress/kendo-angular-tooltip';
import { NavItem } from 'app/navigation';
import { ActionBarComponent } from './action-bar/action-bar.component';
import { ActionBarGroup } from './action-bar/action-bar.interface';

export interface ContentHeaderDateTimeRange {
    from: Date;
    to: Date;
}

@Component({
    selector: 'app-content-header',
    standalone: true,
    imports: [
        ActionBarComponent,
        DateInputsModule,
        NgCommonModule,
        NgIconComponent,
        RouterModule,
        TooltipModule,
        TranslateModule
    ],
    template: `
        <div
            class="mb-2 flex flex-wrap items-start justify-between rounded-lg bg-card pb-1 shadow-md"
            id="content-header">
            <section class="flex flex-col gap-3 px-4">
                <div *ngIf="getBreadcrumbs().length" class="flex items-center gap-2 text-xs">
                    <a
                        class="flex items-center gap-1 text-foreground/40 hover:text-link"
                        aria-current="page"
                        [routerLink]="['/']">
                        <ng-icon kendoTooltip title="{{ 'Home' | translate }}" size="12px" name="faSolidHouse" />
                    </a>
                    <ng-container *ngFor="let item of getBreadcrumbs()">
                        <div *ngIf="item.icon" class="pt-1 text-foreground/10">
                            <ng-icon size="1rem" name="faSolidAngleRight" />
                        </div>
                        <a
                            class="flex items-center gap-1 text-foreground/40"
                            aria-current="page"
                            [ngClass]="{ 'flex gap-1': true, 'hover:text-link': item.route }"
                            [routerLink]="item.route ? [item.route] : null">
                            <ng-icon
                                *ngIf="!icon"
                                size="12px"
                                [name]="title && item?.route ? item?.icon : item?.icon" />
                            <ng-icon *ngIf="icon" size="12px" [name]="title && item?.route ? item?.icon : icon" />
                            <div>{{ item.title.replace('-', ' ') | titlecase | translate }}</div>
                        </a>
                    </ng-container>
                </div>
            </section>
            <hr *ngIf="getBreadcrumbs().length" class="w-full border-background-darker" />
            <div class="flex w-full flex-wrap items-center justify-between gap-2 px-4 py-2 text-foreground">
                <div class="flex flex-row items-center gap-3">
                    <ng-icon size="24px" [name]="icon ? icon : getCurrentNavItem()?.icon" />
                    <div>
                        <h1 class="text-lg text-muted">
                            {{ (title ? title : getCurrentNavItem()?.title) | translate }}
                        </h1>
                        <ng-content select="[subtitle]" />
                    </div>
                </div>
                <ng-content select="[metadata-1]" />
                <div class="flex flex-wrap items-end justify-end gap-2">
                    @if (dateTimeRange(); as range) {
                        <div class="flex flex-wrap items-end gap-2 text-xs text-muted">
                            <label class="flex flex-col gap-1">
                                <span>{{ 'From' | translate }}</span>
                                <kendo-datetimepicker
                                    class="bg-input text-input-foreground"
                                    [attr.aria-label]="'From' | translate"
                                    [format]="dateTimeRangeFormat"
                                    [max]="range.to"
                                    [disabled]="dateTimeRangeDisabled()"
                                    [value]="range.from"
                                    (valueChange)="onDateTimeRangeFromChange($event)">
                                    <kendo-datetimepicker-messages
                                        today="{{ 'Today' | translate }}"
                                        dateTab="{{ 'Date' | translate }}"
                                        timeTab="{{ 'Time' | translate }}"
                                        now="{{ 'Now' | translate }}"
                                        cancel="{{ 'Cancel' | translate }}"
                                        accept="{{ 'Accept' | translate }}" />
                                </kendo-datetimepicker>
                            </label>
                            <label class="flex flex-col gap-1">
                                <span>{{ 'To' | translate }}</span>
                                <kendo-datetimepicker
                                    class="bg-input text-input-foreground"
                                    [attr.aria-label]="'To' | translate"
                                    [format]="dateTimeRangeFormat"
                                    [min]="range.from"
                                    [disabled]="dateTimeRangeDisabled()"
                                    [value]="range.to"
                                    (valueChange)="onDateTimeRangeToChange($event)">
                                    <kendo-datetimepicker-messages
                                        today="{{ 'Today' | translate }}"
                                        dateTab="{{ 'Date' | translate }}"
                                        timeTab="{{ 'Time' | translate }}"
                                        now="{{ 'Now' | translate }}"
                                        cancel="{{ 'Cancel' | translate }}"
                                        accept="{{ 'Accept' | translate }}" />
                                </kendo-datetimepicker>
                            </label>
                        </div>
                    }
                    <app-action-bar [items]="actionBar" />
                </div>
            </div>
            <ng-content select="[metadata-2]" />
        </div>
    `,
    changeDetection: ChangeDetectionStrategy.OnPush
})
export class ContentHeaderComponent {
    navigationService = inject(NavigationService);
    readonly dateTimeRange = input<ContentHeaderDateTimeRange | null>(null);
    readonly dateTimeRangeDisabled = input(false);
    readonly dateTimeRangeChange = output<ContentHeaderDateTimeRange>();
    readonly dateTimeRangeFormat = 'dd. MM. yyyy HH:mm';

    @Input() title: string;
    @Input() icon: IconKey;
    @Input() actionBar: ActionBarGroup[];
    /**
     * Pass this if you want to override the breadcrumbs
     */
    @Input() breadcrumbs?: Breadcrumb[];
    /**
     * Pass this if you want to override the currentNavItem
     */
    @Input() currentNavItem: NavItem;

    getBreadcrumbs(): Breadcrumb[] {
        return this.breadcrumbs ?? this.navigationService.breadcrumbs();
    }

    getCurrentNavItem(): NavItem {
        return this.currentNavItem ?? this.navigationService.currentNavItem();
    }

    onDateTimeRangeFromChange(from: Date | null): void {
        const range = this.dateTimeRange();

        if (!range || !from) {
            return;
        }

        this.dateTimeRangeChange.emit({ from, to: range.to });
    }

    onDateTimeRangeToChange(to: Date | null): void {
        const range = this.dateTimeRange();

        if (!range || !to) {
            return;
        }

        this.dateTimeRangeChange.emit({ from: range.from, to });
    }
}
